'use client';

import { clsx } from 'clsx';
import { capitalize } from 'lodash';
import { FC } from 'react';

import { BADGE } from 'src/general/components/BadgeIllustration/BadgeIllustration.type';
import { Image } from 'src/general/components/Image/Image';
import { useModal } from 'src/hooks/useModal';
import { getManufacturerWarranty } from 'src/pdp/helpers/manufacturerWarranty.helper';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';

import { WarrantyInfoModal } from '../WarrantyInfoModal/WarrantyInfoModal';

/**
 * TODO - Hardcoded as a temporary solution.
 * We will revisit how extended manufacturer warranty status is determined when the feature is more well-defined.
 */
const makesWithExtendedManufacturerWarranty = ['Toyota', 'Mitsubishi'];

interface WarrantyInfoProps {
  productDetails: ProductDetailsPayload;
  hasManufacturerWarranty: boolean; // applied for car with at least 3 months and 5000km/unlimited remaining warranty - manufacturerWarranty.helper
  extendedCoverageAvailable: boolean; // applied for cars with < 160000 odo, this is EC from Carma - refer to CarOverview.tsx.
}

export const WarrantyInfo: FC<WarrantyInfoProps> = ({
  productDetails,
  hasManufacturerWarranty,
  extendedCoverageAvailable,
}) => {
  const { isVisible, open, close } = useModal();
  const formattedMake = capitalize(productDetails.make);
  const inInMakeWithExtendedManufacturerWarranty = makesWithExtendedManufacturerWarranty.includes(formattedMake);
  const extendedManufacturerWarrantyAvailable =
    extendedCoverageAvailable && hasManufacturerWarranty && inInMakeWithExtendedManufacturerWarranty; // this is EC from the make manufacturer

  return (
    <>
      <div className="c-fs-caption -mx-2 mt-6 flex flex-col gap-3 rounded-lg bg-bac-tie2 p-2 md:mx-0 md:flex-row">
        {hasManufacturerWarranty && (
          <div className="flex flex-1 items-center gap-3 py-2 pr-2">
            <Image src={BADGE.WARRANTY} alt="Warranty" width={56} height={56} />
            <div>
              <h6 className="font-extrabold">
                Remaining manufacturer warranty
                {inInMakeWithExtendedManufacturerWarranty && '*'}
              </h6>
              <p>{getManufacturerWarranty(productDetails)}</p>
            </div>
          </div>
        )}

        {extendedCoverageAvailable && (
          <div
            className={clsx('flex flex-1 items-center gap-3', {
              ['rounded-md bg-bac-tie1']: hasManufacturerWarranty,
            })}
          >
            <Image src={BADGE.EXTENDED_COVERAGE_WARRANTY} alt="Extended coverage warranty" width={56} height={56} />

            <div>
              {hasManufacturerWarranty
                ? 'Additional mechanical protection and 24/7 roadside assistance with Carma Extended Coverage.'
                : 'Get mechanical protection plus 24/7 roadside assistance with Carma Extended Coverage.'}
              &ensp;
              <button
                className="-m-2 inline-block cursor-pointer p-2 font-extrabold underline hover:no-underline focus:no-underline"
                onClick={open}
              >
                Learn more
              </button>
            </div>
          </div>
        )}
      </div>
      {extendedManufacturerWarrantyAvailable && (
        <div className="mt-2">
          <p className="c-fs-caption text-tex-def-tie2">
            *This car may also be eligible for extended manufacturer warranty, please check with {formattedMake}
          </p>
        </div>
      )}
      <WarrantyInfoModal isOpen={isVisible} close={close} />
    </>
  );
};
